import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';

export const InterceptorSkipHeader = 'X-Skip-Interceptor';

type ShortLang = 'pt' | 'en' | 'es';

const BACK_LANG_MAP: Record<ShortLang, string> = {
  pt: 'pt-BR',
  en: 'en-US',
  es: 'es-ES',
};

const GLOBAL_LANG_KEY = 'appLang';
const userLangKey = (userId: string) => `appLang_${userId}`;

function normalizeLang(input?: string | null): ShortLang {
  const raw = (input ?? '').toLowerCase();
  if (raw.startsWith('pt')) return 'pt';
  if (raw.startsWith('en')) return 'en';
  if (raw.startsWith('es')) return 'es';
  return 'pt';
}

function getUserId(storage: LocalStorageService): string | null {
  const byKey = storage.get('user_id') as string | null;
  if (byKey) return byKey;

  const user =
    (storage.get('user') as { id?: string } | null) ||
    (storage.get('authUser') as { id?: string } | null) ||
    (storage.get('session') as { id?: string } | null);

  return user?.id ?? null;
}

function resolveHeaderLang(storage: LocalStorageService): string {
  const uid = getUserId(storage);
  const userKey = uid ? userLangKey(uid) : null;

  const userLangRaw = userKey ? (storage.get(userKey) as string | null) : null;
  const globalLangRaw = storage.get(GLOBAL_LANG_KEY) as string | null;
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'pt') || 'pt';

  const short = normalizeLang(userLangRaw ?? globalLangRaw ?? nav);
  return BACK_LANG_MAP[short];
}

@Injectable({ providedIn: 'root' })
export class AuthInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly _storage: LocalStorageService,
    private readonly _router: Router,
    private readonly _authService: AuthService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const langHeader = resolveHeaderLang(this._storage);
    request = request.clone({
      setHeaders: {
        'X-Lang': langHeader,
        'Accept-Language': langHeader,
      },
    });


    if (request.headers.has(InterceptorSkipHeader)) {
      const headers = request.headers.delete(InterceptorSkipHeader);
      return next.handle(request.clone({ headers }));
    }

    return of(this._storage.get('access_token')).pipe(
      mergeMap(() => {
        const token = this._storage.get('access_token') as string | null;
        const connectionId = (this._storage.get('connection') as string | null) ?? '';

        if (token) {
          const reqWithToken = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
              'connection-id': connectionId,
            },
          });

          return next.handle(reqWithToken).pipe(
            catchError((err) => {
              if (err instanceof HttpErrorResponse && err.status === 401) {
                return this.handle401Error(reqWithToken, next, connectionId);
              }
              return throwError(() => err);
            })
          );
        }


        return next.handle(request).pipe(
          catchError((err) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              return this.handle401Error(request, next, connectionId);
            }
            return throwError(() => err);
          })
        );
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    connectionId?: string
  ): Observable<HttpEvent<any>> {
    const refreshToken = this._storage.get('refresh_token') as string | null;

    if (!refreshToken) {
      this.clearSessionAndRedirect();
      return throwError(() => new Error('No refresh token'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authService.refresh().pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;

          this._storage.set('access_token', res.token);
          if (res.refresh_token) {
            this._storage.set('refresh_token', res.refresh_token);
          }

          this.refreshTokenSubject.next(res.token);

          return next.handle(this.addTokenToRequest(request, res.token, connectionId));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.clearSessionAndRedirect();
          return throwError(() => err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenToRequest(request, token!, connectionId)))
    );
  }

  private addTokenToRequest(
    request: HttpRequest<any>,
    token: string,
    connectionId?: string
  ): HttpRequest<any> {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (connectionId) headers['connection-id'] = connectionId;
    return request.clone({ setHeaders: headers });
  }


  private clearSessionAndRedirect(): void {
    this._storage.remove('access_token');
    this._storage.remove('refresh_token');
    this._storage.remove('user');
    this._storage.remove('authUser');
    this._storage.remove('session');

    this._router.navigate(['public/login']).then();
  }
}
